import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { map } from 'rxjs/operators';
import { MatDrawer, MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { Observable } from 'rxjs';
import { scaleIn400ms } from '@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '@vex/animations/fade-in-right.animation';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MailAccount, MailService } from '../services/mail.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { MailSidenavComponent } from '../components/mail-sidenav/mail-sidenav.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MailListComponent } from './mail-list.component';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatBadgeModule } from '@angular/material/badge';
import { ComposeDraft, MailComposeComponent } from '../components/mail-compose/mail-compose.component';
import { Mail } from '../interfaces/mail.interface';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

interface ComposeWindow {
  id: number;
  mode: 'compose' | 'reply' | 'replyAll' | 'forward';
  mail?: Mail;
  draft?: ComposeDraft | null;
  minimized: boolean;
  maximized: boolean;
}

@Component({
    selector: 'vex-mail',
    templateUrl: './mail.component.html',
    styleUrls: ['./mail.component.scss'],
    animations: [scaleIn400ms, fadeInRight400ms],
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatSidenavModule,
        NgIf,
        NgFor,
        MatIconModule,
        MatButtonModule,
        MailSidenavComponent,
        ReactiveFormsModule,
        NgClass,
        AsyncPipe,
        MatInputModule,
        MailListComponent,
        MatSelectModule,
        MatProgressBarModule,
        MatBadgeModule,
        MailComposeComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MailComponent implements OnInit {
  isDesktop$ = this.layoutService.isDesktop$;
  ltLg$ = this.layoutService.ltLg$;
  drawerMode$: Observable<MatDrawerMode> = this.isDesktop$.pipe(map((isDesktop) => (isDesktop ? 'side' : 'over')));
  isVerticalLayout$: Observable<boolean> = this.configService.select((config) => config.layout === 'vertical');

  drawerOpen = true;
  searchCtrl = new UntypedFormControl();
  composeWindows: ComposeWindow[] = [];
  private composeId = 0;

  @ViewChild(MatDrawer, { static: true }) private drawer?: MatDrawer;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly layoutService: VexLayoutService,
    public readonly mailService: MailService,
    private readonly configService: VexConfigService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.mailService.loadAccess().subscribe({
      next: (access) => {
        if (!access?.is_mail_enabled) {
          this.snackBar.open('Mail module is not licensed for this company.', 'Close', { duration: 3500 });
          this.dialog.closeAll();
          return;
        }

        this.mailService.loadAccounts().subscribe({
          next: () => this.mailService.loadMessages()
        });
      },
      error: () => {
        this.snackBar.open('Mail module is not available for this company.', 'Close', { duration: 3500 });
        this.dialog.closeAll();
      }
    });

    this.isDesktop$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isDesktop) => {
      if (isDesktop) {
        this.drawer?.open();
      } else {
        this.drawer?.close();
      }
    });

    this.searchCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.mailService.filterValue.next(value || '');
      this.mailService.loadMessages();
    });

    this.mailService.selectedAccountId.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id) {
        this.mailService.loadMessages();
      }
    });

    this.mailService.composeRequests.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((request) => {
      this.openCompose(request.mode, request.mail, request.draft || null);
    });

  }

  openCompose(mode: ComposeWindow['mode'] = 'compose', mail?: Mail, draft?: ComposeDraft | null) {
    this.composeWindows.push({
      id: ++this.composeId,
      mode,
      mail,
      draft: draft || null,
      minimized: false,
      maximized: false
    });
  }

  closeCompose(id: number) {
    this.composeWindows = this.composeWindows.filter((window) => window.id !== id);
  }

  toggleComposeMinimize(id: number, minimized: boolean) {
    this.composeWindows = this.composeWindows.map((window) =>
      window.id === id ? { ...window, minimized, maximized: minimized ? false : window.maximized } : window
    );
  }

  toggleComposeMaximize(id: number, maximized: boolean) {
    this.composeWindows = this.composeWindows.map((window) =>
      window.id === id ? { ...window, maximized, minimized: false } : { ...window, maximized: false }
    );
  }

  trackComposeWindowId(_: number, compose: ComposeWindow) {
    return compose.id;
  }

  selectedAccount(): MailAccount | null {
    const id = this.mailService.selectedAccountId.value;
    return this.mailService.accounts.value.find((account) => Number(account.id) === Number(id)) || null;
  }

  closemail() {
    this.dialog.closeAll();
  }
}
